// Premium Ale Effect (Healing Artifact)
// Featuring High-Density Internal Fluid Simulation
// 1. MATERIALIZE: Shimmering empty mug appears.
// 2. TOAST: Mug tilts slightly right, interior foam sloshes.
// 3. POUR: Mug tips deeply to the left. Physics particles cross the rim and waterfall onto the avatar.
// 4. VANISH: Fades out in place.

import { Particle } from '../ParticleLayer';

// Overall size reduction applied to everything
const GLOBAL_SCALE = 0.5;

const CONFIG = {
    scale: 0.65 * GLOBAL_SCALE, // Mug scale specifically
    materializeDuration: 800,
    toastDuration: 700,
    pourDuration: 1800,
    vanishDuration: 500,
    paths: {
        mugBack: '/assets/effects/ale/mug_back.svg',
        mugFront: '/assets/effects/ale/mug_front.svg',
    }
};

const spriteCache: Record<string, HTMLImageElement> = {};
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image();
        img.src = src;
        spriteCache[src] = img;
    }
    return spriteCache[src];
};

getSprite(CONFIG.paths.mugBack);
getSprite(CONFIG.paths.mugFront);

// ==========================================
// PBD FLUID DYNAMICS (High-Performance Broadphase)
// ==========================================

class FluidDrop {
    id: number;
    kind: 'BEER' | 'FOAM';
    x: number; y: number;
    vx: number; vy: number;
    life: number; size: number;
    location: 'INSIDE' | 'POURED';
    state: 'FALLING' | 'BOUNCING' | 'SLIDING';

    constructor(id: number, startX: number, startY: number, kind: 'BEER' | 'FOAM') {
        this.id = id;
        this.kind = kind;
        this.x = startX + (Math.random() - 0.5) * (40 * GLOBAL_SCALE);
        this.y = startY + (Math.random() - 0.5) * (40 * GLOBAL_SCALE);
        this.vx = (Math.random() - 0.5) * 3 * GLOBAL_SCALE;
        this.vy = Math.random() * 3 * GLOBAL_SCALE;

        this.location = 'INSIDE';
        this.state = 'FALLING';
        this.life = 1.0;

        // Size optimized for dense packing, scaled down
        if (kind === 'FOAM') {
            this.size = (11 + Math.random() * 6) * GLOBAL_SCALE;
        } else {
            this.size = (9 + Math.random() * 5) * GLOBAL_SCALE;
        }
    }

    updateExt(targetX: number, targetY: number) {
        if (this.life <= 0) return;

        // Once poured out of the mug
        if (this.location === 'POURED') {
            const avatarCenterX = targetX;
            const avatarCenterY = targetY;
            const avatarRadius = 65 * GLOBAL_SCALE;

            if (this.state === 'FALLING') {
                this.vy += 0.8 * GLOBAL_SCALE; // World Gravity
                this.x += this.vx;
                this.y += this.vy;

                const dx = this.x - avatarCenterX;
                const dy = this.y - avatarCenterY;
                const distSq = dx * dx + dy * dy;

                // Sphere collision against avatar
                if (distSq < avatarRadius * avatarRadius && this.vy > 0) {
                    const dist = Math.sqrt(distSq);
                    const nx = dx / dist; const ny = dy / dist;

                    if (Math.random() < (this.kind === 'FOAM' ? 0.9 : 0.4)) {
                        // Slime/Sliding (Sticks to avatar curve)
                        this.state = 'SLIDING';
                        this.vx = 0; this.vy = 0;
                        this.x = avatarCenterX + nx * avatarRadius;
                        this.y = avatarCenterY + ny * avatarRadius;
                    } else {
                        // Splash Drop
                        this.state = 'BOUNCING';
                        this.vx = this.vx * 0.3 + nx * 5 * GLOBAL_SCALE + (Math.random() - 0.5) * 5 * GLOBAL_SCALE;
                        this.vy = -this.vy * 0.4 + ny * 5 * GLOBAL_SCALE - Math.random() * 4 * GLOBAL_SCALE;
                        this.x += nx * 5 * GLOBAL_SCALE; this.y += ny * 5 * GLOBAL_SCALE;
                        this.size *= 0.6; // Drops break apart visually
                    }
                } else if (this.y > targetY + 220 * GLOBAL_SCALE) {
                    // Hit floor
                    this.state = 'BOUNCING';
                    this.vy = -this.vy * 0.2; this.vx *= 0.5;
                    this.y = targetY + 220 * GLOBAL_SCALE;
                }
            } else if (this.state === 'BOUNCING') {
                this.vy += 0.8 * GLOBAL_SCALE;
                this.x += this.vx;
                this.y += this.vy;
                if (this.y > targetY + 220 * GLOBAL_SCALE) {
                    this.y = targetY + 220 * GLOBAL_SCALE;
                    this.vy *= -0.2;
                    this.vx *= 0.7;
                }
                this.life -= 0.02;
            } else if (this.state === 'SLIDING') {
                const dx = this.x - avatarCenterX;
                const dy = this.y - avatarCenterY;
                let angle = Math.atan2(dy, dx);

                // Gravity pulls sliding drops down the curve
                if (angle > -Math.PI / 2 && angle < Math.PI / 2) angle += (this.kind === 'FOAM' ? 0.01 : 0.03) + Math.random() * 0.01;
                else angle -= (this.kind === 'FOAM' ? 0.01 : 0.03) + Math.random() * 0.01;

                if (angle > Math.PI / 2 - 0.1 && angle < Math.PI / 2 + 0.1) {
                    // Reached the bottom of the curve, drip!
                    this.state = 'FALLING';
                    this.vx = (Math.random() - 0.5) * GLOBAL_SCALE;
                    this.vy = 1 * GLOBAL_SCALE;
                } else {
                    this.x = avatarCenterX + Math.cos(angle) * avatarRadius;
                    this.y = avatarCenterY + Math.sin(angle) * avatarRadius;
                }
                // Foam trails behind, stays longer
                this.life -= (this.kind === 'FOAM' ? 0.003 : 0.015);
                if (Math.random() < 0.1) this.size *= 0.96;
            }
        }
    }
}

class HealingCross {
    x: number; y: number;
    life: number; scale: number;
    vy: number;

    constructor(x: number, y: number) {
        this.x = x + (Math.random() - 0.5) * 80 * GLOBAL_SCALE;
        this.y = y + (Math.random() - 0.5) * 40 * GLOBAL_SCALE;
        this.life = 1.0;
        this.scale = (0.5 + Math.random()) * GLOBAL_SCALE;
        this.vy = -(1 + Math.random() * 2) * GLOBAL_SCALE;
    }

    update() {
        this.y += this.vy;
        this.life -= 0.015;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        ctx.save();
        ctx.globalAlpha = Math.max(0, this.life);
        ctx.translate(this.x, this.y);
        ctx.scale(this.scale * this.life, this.scale * this.life);

        ctx.shadowColor = '#4ade80';
        ctx.shadowBlur = 15;
        ctx.fillStyle = '#86efac';

        const s = 15;
        const w = 6;
        ctx.beginPath();
        ctx.fillRect(-w / 2, -s, w, s * 2);
        ctx.fillRect(-s, -w / 2, s * 2, w);
        ctx.restore();
    }
}

export type AleEffect = {
    kind: 'ale'
    t: number
    tx: number; ty: number;
    data?: {
        phase: 'MATERIALIZE' | 'TOAST' | 'POUR' | 'VANISH'
        drops: FluidDrop[]
        crosses: HealingCross[]
        startX: number
        spawnY: number
        spawnIndex: number
    }
}

export const createAleEffect = (tx: number, ty: number): AleEffect => ({
    kind: 'ale', t: 0, tx, ty
});

export const renderAleEffect = (
    eff: AleEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean => {
    if (!eff.data) {
        // Dynamic positioning so it stays visible and correctly oriented
        eff.data = {
            phase: 'MATERIALIZE',
            drops: [],
            crosses: [],
            // Always to the right so tilt-left works, and bounded slightly so it doesn't go off screen edge
            startX: eff.tx + 140,
            // Ensure minimum Y height so the top of the cup doesn't clip off the screen
            spawnY: Math.max(160, eff.ty - 160),
            spawnIndex: 0
        };
    }

    const d = eff.data;
    const tMat = CONFIG.materializeDuration;
    const tToast = tMat + CONFIG.toastDuration;
    const tPour = tToast + CONFIG.pourDuration;
    const tTotal = tPour + CONFIG.vanishDuration;

    if (eff.t < tMat) d.phase = 'MATERIALIZE';
    else if (eff.t < tToast) d.phase = 'TOAST';
    else if (eff.t < tPour) d.phase = 'POUR';
    else d.phase = 'VANISH';

    ctx.save();

    let alpha = 1;
    let currentX = d.startX;
    let currentY = d.spawnY;
    let rotation = 0;

    const scale = CONFIG.scale;
    // Exact Inner volume of the SVG mapped around the center rotation pivot
    // Same values since the rotation anchor point hasn't conceptually moved relative to the artwork itself.
    const leftInner = -165 * scale;
    const rightInner = 105 * scale;
    const bottomInner = 92 * scale;
    const topOuter = -260 * scale;

    // Animate Phase
    if (d.phase === 'MATERIALIZE') {
        const p = eff.t / tMat;
        alpha = p;
        currentY = d.spawnY + 60 * GLOBAL_SCALE * (1 - p);

        // Spawn 400 BEER, then 100 FOAM dynamically to fill up
        const targetCount = Math.floor(p * 500);
        while (d.spawnIndex < targetCount && d.spawnIndex < 500) {
            const kind = d.spawnIndex > 400 ? 'FOAM' : 'BEER';
            const sx = currentX + (leftInner + rightInner) / 2 + (Math.random() - 0.5) * 100 * GLOBAL_SCALE;
            const sy = currentY + topOuter + (100 + Math.random() * 50) * GLOBAL_SCALE;
            d.drops.push(new FluidDrop(d.spawnIndex, sx, sy, kind));
            d.spawnIndex++;
        }
    }
    else if (d.phase === 'TOAST') {
        alpha = 1;
        const p = (eff.t - tMat) / CONFIG.toastDuration;
        if (p < 0.4) rotation = (p / 0.4) * (Math.PI / 10);
        else if (p < 0.6) rotation = (Math.PI / 10) - ((p - 0.4) / 0.2) * (Math.PI / 5);
        else rotation = -(Math.PI / 10) * (1 - (p - 0.6) / 0.4);
    }
    else if (d.phase === 'POUR') {
        alpha = 1;
        const p = (eff.t - tToast) / CONFIG.pourDuration;
        const maxTilt = -Math.PI / 2.2;

        if (p < 0.15) rotation = maxTilt * (p / 0.15);
        else if (p > 0.85) rotation = maxTilt * (1 - (p - 0.85) / 0.15);
        else {
            rotation = maxTilt;
            if (p > 0.2 && Math.random() < 0.2) {
                d.crosses.push(new HealingCross(eff.tx, eff.ty));
            }
            if (p > 0.2 && p < 0.8) {
                ctx.save();
                ctx.globalCompositeOperation = 'screen';
                ctx.fillStyle = `rgba(134, 239, 172, ${0.18 * Math.sin((p - 0.2) / 0.6 * Math.PI)})`;
                ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);
                ctx.restore();
            }
        }
    }
    else if (d.phase === 'VANISH') {
        const p = (eff.t - tPour) / CONFIG.vanishDuration;
        alpha = 1 - p;
        if (alpha < 0) alpha = 0;
        // Float very gently upwards without moving too fast
        currentY -= p * 20 * GLOBAL_SCALE;
    }

    // INTERNAL PHYSICS SIMULATION
    // ===========================
    const cos = Math.cos(-rotation), sin = Math.sin(-rotation);
    const fixCos = Math.cos(rotation), fixSin = Math.sin(rotation);
    const gravX = 0, gravY = 1.2 * GLOBAL_SCALE;

    d.drops.forEach(drop => {
        if (drop.location === 'INSIDE') {
            let g = gravY;
            if (drop.kind === 'FOAM') g = -0.5 * GLOBAL_SCALE;

            drop.vy += g;
            drop.x += drop.vx;
            drop.y += drop.vy;

            let dx = drop.x - currentX;
            let dy = drop.y - currentY;
            let lx = dx * cos - dy * sin;
            let ly = dx * sin + dy * cos;

            let r = drop.size;
            let hitWall = false;

            if (lx < leftInner + r) { lx = leftInner + r; hitWall = true; }
            if (lx > rightInner - r) { lx = rightInner - r; hitWall = true; }
            if (ly > bottomInner - r) { ly = bottomInner - r; hitWall = true; }

            if (drop.kind === 'FOAM' && hitWall && rotation === 0) {
                drop.vy *= 0.5;
                drop.vx *= 0.5;
            }

            if (ly < topOuter) {
                drop.location = 'POURED';
                const distLip = Math.abs(lx - leftInner);
                if (distLip < 60 * GLOBAL_SCALE) {
                    drop.vx += fixSin * 6 * GLOBAL_SCALE + (Math.random() - 0.5) * 2 * GLOBAL_SCALE;
                    drop.vy -= fixCos * 6 * GLOBAL_SCALE + (Math.random() - 0.5) * 2 * GLOBAL_SCALE;
                }
            } else if (hitWall) {
                drop.x = currentX + lx * fixCos - ly * fixSin;
                // Bugfix from earlier: this should be currentY
                drop.y = currentY + lx * fixSin + ly * fixCos;
                drop.vx *= 0.8;
                drop.vy *= 0.8;
            }
        }

        drop.updateExt(eff.tx, eff.ty);
    });

    for (let iter = 0; iter < 1; iter++) {
        for (let i = 0; i < d.drops.length; i++) {
            let d1 = d.drops[i];
            if (d1.life <= 0) continue;

            for (let j = i + 1; j < d.drops.length; j++) {
                let d2 = d.drops[j];
                if (d2.life <= 0) continue;

                let dx = d1.x - d2.x;
                if (dx > 25 * GLOBAL_SCALE || dx < -25 * GLOBAL_SCALE) continue;

                let dy = d1.y - d2.y;
                if (dy > 25 * GLOBAL_SCALE || dy < -25 * GLOBAL_SCALE) continue;

                let distSq = dx * dx + dy * dy;
                let minDist = d1.size + d2.size;

                if (distSq < minDist * minDist && distSq > 0.1) {
                    let dist = Math.sqrt(distSq);
                    let overlap = (minDist - dist) * 0.5;
                    let nx = dx / dist; let ny = dy / dist;

                    if (d1.location === 'INSIDE' && d2.location === 'INSIDE') {
                        if (d1.kind === 'FOAM' && d2.kind === 'BEER') {
                            d1.vy -= 1.5 * GLOBAL_SCALE; d1.y -= 2 * GLOBAL_SCALE;
                            d2.vy += 0.5 * GLOBAL_SCALE; d2.y += 1 * GLOBAL_SCALE;
                        } else if (d2.kind === 'FOAM' && d1.kind === 'BEER') {
                            d2.vy -= 1.5 * GLOBAL_SCALE; d2.y -= 2 * GLOBAL_SCALE;
                            d1.vy += 0.5 * GLOBAL_SCALE; d1.y += 1 * GLOBAL_SCALE;
                        }
                    }

                    d1.x += nx * overlap; d1.y += ny * overlap;
                    d2.x -= nx * overlap; d2.y -= ny * overlap;

                    let dvx = d1.vx - d2.vx; let dvy = d1.vy - d2.vy;
                    let friction = 0.2;
                    d1.vx -= dvx * friction; d1.vy -= dvy * friction;
                    d2.vx += dvx * friction; d2.vy += dvy * friction;
                }
            }
        }
    }

    d.drops = d.drops.filter(drop => drop.life > 0);

    // DRAWING
    // ===========================

    // SVG now has viewBox="-150 -150 800 950" and width/height=800/950.
    // The visual pivot originally at (250, 433) relative to the bounding box is now at (400, 583).
    // So we translate by (-400, -583) specifically to anchor the SVG exactly where the physics scale operates.
    const drawSvgLayer = (img: HTMLImageElement) => {
        if (img.complete && img.naturalWidth > 0) {
            ctx.drawImage(img, -400, -583, 800, 950);
        }
    };

    if (alpha > 0) {
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(currentX, currentY);
        ctx.rotate(rotation);
        ctx.scale(CONFIG.scale, CONFIG.scale);
        drawSvgLayer(getSprite(CONFIG.paths.mugBack));
        ctx.restore();
    }

    ctx.save();
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';

    // Draw Beer Blob unified without geometry spikes
    ctx.fillStyle = '#f59e0b'; // solid amber base

    // Inside beer fades with the mug
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    d.drops.filter(d => d.kind === 'BEER' && d.location === 'INSIDE').forEach(drop => {
        let stretch = drop.state === 'FALLING' ? 1 + Math.hypot(drop.vx, drop.vy) * 0.1 * (1 / GLOBAL_SCALE) : 1;
        let shrinkY = drop.state === 'FALLING' ? Math.max(0.6, 1 - Math.hypot(drop.vx, drop.vy) * 0.03 * (1 / GLOBAL_SCALE)) : 1;
        let pRotation = drop.state === 'FALLING' ? Math.atan2(drop.vy, drop.vx) : 0;

        let radius = drop.size;
        ctx.moveTo(drop.x + radius * stretch * Math.cos(pRotation), drop.y + radius * stretch * Math.sin(pRotation));
        ctx.ellipse(drop.x, drop.y, radius * stretch, radius * shrinkY, pRotation, 0, Math.PI * 2);
    });
    ctx.fill();

    // Poured beer uses full opacity but culls dead drops
    ctx.globalAlpha = 1;
    ctx.beginPath();
    d.drops.filter(d => d.kind === 'BEER' && d.location === 'POURED').forEach(drop => {
        if (drop.life <= 0) return;
        let stretch = drop.state === 'FALLING' ? 1 + Math.hypot(drop.vx, drop.vy) * 0.1 * (1 / GLOBAL_SCALE) : 1;
        let shrinkY = drop.state === 'FALLING' ? Math.max(0.6, 1 - Math.hypot(drop.vx, drop.vy) * 0.03 * (1 / GLOBAL_SCALE)) : 1;
        let pRotation = drop.state === 'FALLING' ? Math.atan2(drop.vy, drop.vx) : 0;

        let radius = drop.size;
        ctx.moveTo(drop.x + radius * stretch * Math.cos(pRotation), drop.y + radius * stretch * Math.sin(pRotation));
        ctx.ellipse(drop.x, drop.y, radius * stretch, radius * shrinkY, pRotation, 0, Math.PI * 2);
    });
    ctx.fill();

    // Beer Bubbles ONLY on top of beer liquid (glowing)
    ctx.fillStyle = '#ffffff';
    ctx.shadowBlur = 8;
    ctx.shadowColor = '#fef08a';
    ctx.beginPath();
    d.drops.filter(d => d.kind === 'BEER').forEach(drop => {
        if (drop.id % 15 === 0 && drop.life > 0) {
            let ox = Math.sin(eff.t * 0.01 + drop.id) * drop.size * 0.5;
            let oy = -Math.cos(eff.t * 0.01 + drop.id) * drop.size * 0.5;
            let rad = (1.5 + Math.sin(eff.t * 0.02 + drop.id)) * GLOBAL_SCALE;
            if (rad > 0.5) {
                // If it's pouring or splashed, use its own specific life alpha, else use mug alpha
                let localAlpha = drop.location === 'INSIDE' ? alpha : drop.life;
                if (localAlpha > 0.2) {
                    ctx.moveTo(drop.x + ox + rad, drop.y + oy);
                    ctx.arc(drop.x + ox, drop.y + oy, rad, 0, Math.PI * 2);
                }
            }
        }
    });
    ctx.fill();
    ctx.shadowBlur = 0; // reset

    // Draw Foam Blob separately
    ctx.fillStyle = '#fef9c3';

    ctx.globalAlpha = alpha;
    ctx.beginPath();
    d.drops.filter(d => d.kind === 'FOAM' && d.location === 'INSIDE').forEach(drop => {
        ctx.moveTo(drop.x + drop.size, drop.y);
        ctx.arc(drop.x, drop.y, drop.size, 0, Math.PI * 2);
    });
    ctx.fill();

    ctx.globalAlpha = 1;
    ctx.beginPath();
    d.drops.filter(d => d.kind === 'FOAM' && d.location === 'POURED').forEach(drop => {
        if (drop.life <= 0) return;
        ctx.moveTo(drop.x + drop.size, drop.y);
        ctx.arc(drop.x, drop.y, drop.size, 0, Math.PI * 2);
    });
    ctx.fill();

    ctx.restore();

    // Draw Front of Mug 
    if (alpha > 0) {
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(currentX, currentY);
        ctx.rotate(rotation);
        ctx.scale(CONFIG.scale, CONFIG.scale);
        drawSvgLayer(getSprite(CONFIG.paths.mugFront));
        ctx.restore();
    }

    d.crosses.forEach(c => c.update());
    d.crosses = d.crosses.filter(c => c.life > 0);
    d.crosses.forEach(c => c.draw(ctx));

    // Effect lives while t < tTotal OR particles are still dripping down the face
    return eff.t < tTotal || d.drops.length > 0 || d.crosses.length > 0;
};
